import { useCallback, useEffect, useState } from 'react';
import { fetchHeatingSchedule, putHeatingEvent, deleteHeatingEvent, solveSchedule } from '../api/portal';
import { sortByTargetTime } from '../utils/schedule';

// The comfort objectives for one device: the single source of the schedule for
// the whole portal (the table, the chart overlay and the planned actions).
// Every change re-solves, then refetches so preheat_start is current, then
// bumps `version` so forecast views refetch too.
export default function useHeatingSchedule(deviceId) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null); // 'saving' | 'solving' | null
    const [error, setError] = useState(null);
    const [version, setVersion] = useState(0);

    const load = useCallback(async () => {
        if (!deviceId) { setEvents([]); return; }
        setLoading(true);
        try {
            setEvents(sortByTargetTime(await fetchHeatingSchedule(deviceId)));
            setError(null);
        } catch (e) {
            setError(`Could not load the schedule: ${e.message}`);
        }
        setLoading(false);
    }, [deviceId]);

    useEffect(() => { load(); }, [load]);

    const resolve = useCallback(async () => {
        if (!deviceId) return;
        setBusy(b => b || 'solving');
        try {
            await solveSchedule(deviceId);
        } catch (e) {
            setError(`Could not re-solve the schedule: ${e.message}`);
        }
        await load();
        setVersion(v => v + 1);
        setBusy(null);
    }, [deviceId, load]);

    const addEvent = useCallback(async ({ targetTime, targetTemperature, label, durationMinutes }) => {
        setBusy('saving');
        try {
            await putHeatingEvent({
                device_id: deviceId,
                target_time: new Date(targetTime).toISOString(),
                target_temperature: parseFloat(targetTemperature),
                label: label || null,
                duration_minutes: parseInt(durationMinutes, 10) || 0,
            });
        } catch (e) {
            setError(`Could not add the event: ${e.message}`);
            setBusy(null);
            return false;
        }
        await resolve();
        return true;
    }, [deviceId, resolve]);

    const removeEvent = useCallback(async (eventId) => {
        setBusy('solving');
        try {
            await deleteHeatingEvent(deviceId, eventId);
        } catch (e) {
            setError(`Could not remove the event: ${e.message}`);
            setBusy(null);
            return;
        }
        await resolve();
    }, [deviceId, resolve]);

    return { events, loading, busy, error, version, addEvent, removeEvent, resolve };
}
